import axios from 'axios';
import { ref } from 'vue';

const INITIAL_LOAD_TIMEOUT_MS = 30000;
/** Idle period required after the last in-flight request before dismissing the loader */
const INITIAL_LOAD_SETTLE_MS = 600;

export const pendingRequestCount = ref(0);
export const hasSeenApiRequest = ref(false);
export const isInitialLoadComplete = ref(false);
export const hasRouteMounted = ref(false);

let appMounted = false;
let settleTimer = null;

const clearSettleTimer = () => {
  if (settleTimer) {
    clearTimeout(settleTimer);
    settleTimer = null;
  }
};

export const markInitialLoadComplete = () => {
  isInitialLoadComplete.value = true;
  clearSettleTimer();
};

const isBootstrapReady = () => appMounted && hasRouteMounted.value;

const scheduleSettleTimer = () => {
  if (settleTimer || isInitialLoadComplete.value) return;

  settleTimer = setTimeout(() => {
    settleTimer = null;
    if (isInitialLoadComplete.value || pendingRequestCount.value > 0 || !isBootstrapReady()) {
      return;
    }
    markInitialLoadComplete();
  }, INITIAL_LOAD_SETTLE_MS);
};

const tryCompleteInitialLoad = () => {
  if (isInitialLoadComplete.value || !isBootstrapReady()) return;

  if (pendingRequestCount.value > 0) {
    clearSettleTimer();
    return;
  }

  scheduleSettleTimer();
};

export const signalAppMounted = () => {
  appMounted = true;
  tryCompleteInitialLoad();
};

export const signalRouteMounted = () => {
  hasRouteMounted.value = true;
  tryCompleteInitialLoad();
};

const trackRequestStart = () => {
  hasSeenApiRequest.value = true;
  pendingRequestCount.value += 1;
  clearSettleTimer();
};

const trackRequestEnd = () => {
  pendingRequestCount.value = Math.max(0, pendingRequestCount.value - 1);
  tryCompleteInitialLoad();
};

setTimeout(() => {
  if (!isInitialLoadComplete.value) {
    markInitialLoadComplete();
  }
}, INITIAL_LOAD_TIMEOUT_MS);

const appUrl = window.location.origin;
const urls = import.meta.env.VITE_LOCAL_HTTP_URLS
  ? import.meta.env.VITE_LOCAL_HTTP_URLS.split(',')
  : ['http://localhost:3000', 'http://10.0.2.2:3000'];

const baseUrl = urls.includes(appUrl)
  ? import.meta.env.VITE_APP_API_ENDPOINT
  : `${appUrl}/spa/`;

const apiClient = axios.create({
  baseURL: baseUrl,
  // withCredentials: true,
  headers: {
    Accept: 'application/json, text/plain, */*',
    'Content-type': 'application/json',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
  },
});

apiClient.interceptors.request.use((config) => {
  trackRequestStart();

  const xsrftoken = sessionStorage.getItem('xsrftoken');
  const sessiontoken = sessionStorage.getItem('sessiontoken');

  if (xsrftoken) {
    config.headers.set('X-XSRF-TOKEN', xsrftoken);
  }

  if (sessiontoken) {
    config.headers.set('Session-Token', sessiontoken);
  }

  return config;
});

apiClient.interceptors.response.use(
  (res) => {
    trackRequestEnd();

    if (res.headers.get('resettokens')) {
      sessionStorage.removeItem('xsrftoken');
      sessionStorage.removeItem('sessiontoken');
    }

    const xsrf = res.headers.get('xsrftoken');
    if (xsrf && sessionStorage.getItem('xsrftoken') === null) {
      sessionStorage.setItem('xsrftoken', xsrf);
    }

    const session = res.headers.get('sessiontoken');
    if (session && sessionStorage.getItem('sessiontoken') === null) {
      sessionStorage.setItem('sessiontoken', session);
    }

    return res;
  },
  (error) => {
    trackRequestEnd();
    return Promise.reject(error);
  },
);

export default apiClient;
